import {
  calculateFirstHomeDuty,
  calculateTransferDuty,
  stampDutyRulesFor,
} from "../stamp-duty/index.js";
import { calculateLmiCost, lmiCostRulesFor } from "../lmi/index.js";
import {
  firstHomeSchemeRulesForDate,
  schemeEligibility,
} from "../first-home-scheme/index.js";
import { calculateFhssEligibility, fhssRulesFor } from "../fhss/index.js";
import type { FinancialYear } from "../types.js";
import {
  type CostLine,
  type HbcState,
  type HomeBuyingCostInput,
  HomeBuyingCostInputSchema,
  type HomeBuyingCostOutput,
  type HomeBuyingCostRuleSet,
  type IndicativeRange,
  type MiscCostItem,
} from "./types.js";

// ---------------------------------------------------------------------------
// Composite NCCP / financial-product handoff guard (ship-condition, hard).
//
// A personalised "total cash needed to buy" figure is EXACTLY the surface a paid
// credit handoff must never ride alongside — it is the archetypal point at which
// a tool would hand off to a mortgage broker / lender, which is the C2 dealing-
// by-arranging kill-line under the NCCP Act. Each child module already enforces
// its own guard; the composer adds this one over the ASSEMBLED output and over
// any surface placements, so the rule holds at the composite surface too.
// ---------------------------------------------------------------------------

// Two distinct lists, deliberately. Bare credit-vertical IDENTIFIERS (broker,
// lender, mortgage…) are forbidden as PLACEMENTS — a partner tile attached to
// the result surface — but those same words legitimately appear in descriptive
// cited copy ("your LENDER's actual LMI may differ", "not a recommendation about
// any home LOAN, lender or broker"). So the bare-identifier list is applied ONLY
// to placements; the OUTPUT content is screened for advice/sales PHRASES that
// could never be innocent (the C2 dealing-by-arranging / "save $X" / "you should"
// kill-lines). This mirrors how the sibling stamp-duty module separates placement
// guarding from output-content guarding.

/** Credit-vertical placement identifiers — forbidden as a result-surface tile. */
const FORBIDDEN_PLACEMENT_TERMS = [
  "broker",
  "lender",
  "home loan",
  "home-loan",
  "homeloan",
  "mortgage",
  "loan",
  "credit",
  "refinance",
  "borrowing",
  "pre-approval",
  "preapproval",
  "finance offer",
  "compare loans",
  "compare lenders",
];

/** Advice / sales PHRASES that may never appear in the assembled output copy. */
const FORBIDDEN_OUTPUT_PHRASES = [
  "apply now",
  "get a loan",
  "refinance now",
  "best rate",
  "lowest rate",
  "we recommend",
  "you should",
  "you can afford",
  "save $",
  "talk to a broker",
  "compare lenders",
  "compare loans",
  "pre-approval",
  "preapproval",
];

export class CreditHandoffError extends Error {
  constructor(identifier: string) {
    super(
      `home-buying-cost composite guard: "${identifier}" is a credit/financial-product ` +
        `handoff and must not reach any surface showing a total-cash-to-buy figure (NCCP C2 ` +
        `dealing-by-arranging). A personalised total is exactly the surface that must not ` +
        `hand off to a credit product.`,
    );
    this.name = "CreditHandoffError";
  }
}

/**
 * Assert the assembled output carries no advice/sales handoff PHRASE, and that
 * no surface PLACEMENT is a credit-vertical identifier. Throws
 * `CreditHandoffError`. The executable form of the composite ship-condition —
 * the rule that holds at the leaf modules also holds at the composite surface.
 * Tested.
 */
export function assertNoCreditHandoff(
  output: unknown,
  surfacePlacements: readonly string[] = [],
): void {
  // Placements: bare credit-vertical identifiers are forbidden.
  for (const p of surfacePlacements) {
    const id = p.toLowerCase();
    for (const term of FORBIDDEN_PLACEMENT_TERMS) {
      if (id.includes(term)) throw new CreditHandoffError(p);
    }
  }
  // Output content: advice/sales phrases that could never be innocent.
  const serialised = JSON.stringify(output).toLowerCase();
  for (const phrase of FORBIDDEN_OUTPUT_PHRASES) {
    if (serialised.includes(phrase)) throw new CreditHandoffError(phrase);
  }
}

const round = (n: number) => Math.round(n);
const mid = (r: IndicativeRange) => round((r.low + r.high) / 2);

/**
 * Derive the AU financial-year key (e.g. "2025-26") for an ISO date. AU FY runs
 * 1 July – 30 June: a date in Jul–Dec belongs to FY `YYYY-(YY+1)`, Jan–Jun to
 * `(YYYY-1)-YY`. Pure string/number arithmetic — no Date.now(); the date is the
 * parameter. v1 ships the 2025-26 child rulesets, so the FY resolves there.
 */
export function fyForDate(isoDate: string): FinancialYear {
  const [yStr, mStr] = isoDate.split("-");
  const year = Number(yStr);
  const month = Number(mStr);
  if (!Number.isInteger(year) || !Number.isInteger(month) || month < 1 || month > 12) {
    throw new Error(`home-buying-cost: invalid assessment date "${isoDate}"`);
  }
  const startYear = month >= 7 ? year : year - 1;
  const endShort = String((startYear + 1) % 100).padStart(2, "0");
  return `${startYear}-${endShort}` as FinancialYear;
}

/** Convert a child indicative misc item into a cost LINE (cash OUT, midpoint). */
function miscLine(item: MiscCostItem): CostLine {
  return {
    id: item.id,
    label: item.label,
    amount: mid(item.range),
    range: item.range,
    indicative: true,
    source: item.source,
    note:
      `Indicative ${item.label.toLowerCase()} — varies by provider and property; ` +
      `shown as a range, not a quote. Source: ${item.source}.`,
  };
}

/**
 * `homeBuyingCost` — the upfront "true cost of buying" COMPOSER. Assembles a
 * single itemised picture from the four shipped modules for a NSW/VIC/QLD
 * first-or-next home buyer. Pure: same inputs + rulesets always yield the same
 * output; the assessment DATE is a parameter (never the clock), so a backdated
 * contract resolves against the rulesets in force then.
 *
 * Composition (which child function produces each line):
 *  - duty   → stamp-duty `calculateFirstHomeDuty` (first-home buyer) or
 *             `calculateTransferDuty` (otherwise). First-home concession applied
 *             inside the child; we read `concessionDuty` + foreign surcharge.
 *  - LMI    → lmi `calculateLmiCost` with `firstHomeGuaranteeEligible` set from
 *             the first-home-scheme 5%-deposit verdict → $0 when the waiver fires.
 *  - scheme → first-home-scheme `schemeEligibility` for the FHOG grant, the 5%
 *             waiver gate, and the cross-scheme stacking notes.
 *  - FHSS   → fhss `calculateFhssEligibility` for the releasable deposit
 *             contribution (surfaced, never netted into completion cash).
 */
export function homeBuyingCost(
  rawInput: HomeBuyingCostInput,
  rules: HomeBuyingCostRuleSet,
  surfacePlacements: readonly string[] = [],
): HomeBuyingCostOutput {
  // Composite guard FIRST — a credit handoff must never reach this surface.
  assertNoCreditHandoff({}, surfacePlacements);

  const input = HomeBuyingCostInputSchema.parse(rawInput);
  const state: HbcState = input.state;
  const fy = fyForDate(input.assessmentDate);

  const loanAmount = Math.max(0, input.purchasePrice - input.deposit);
  const lvr = input.purchasePrice > 0 ? loanAmount / input.purchasePrice : 0;
  const depositFraction =
    input.purchasePrice > 0 ? input.deposit / input.purchasePrice : 0;

  // ── 1. Scheme eligibility (first-home-scheme) — date-keyed period ──
  // Drives: the FHOG grant amount, the 5%-deposit waiver gate (→ LMI=$0), and
  // the cross-scheme stacking notes. We pass NO surface placements here — the
  // composer already guarded its own; a clean call.
  const schemeRules = firstHomeSchemeRulesForDate(input.assessmentDate);
  const scheme = schemeEligibility(
    input.assessmentDate,
    {
      state,
      price: input.purchasePrice,
      income: input.income,
      depositFraction,
      buildType: input.buildType,
      singleParentOrGuardian: input.singleParentOrGuardian,
      firstHomeBuyer: input.firstHomeBuyer,
    },
    schemeRules,
  );
  const fivePercentVerdict = scheme.verdicts.find(
    (v) => v.scheme === "five_percent_deposit",
  );
  const fhogVerdict = scheme.verdicts.find((v) => v.scheme === "fhog");
  const fivePercentEligible = fivePercentVerdict?.eligible ?? false;

  // ── 2. Transfer (stamp) duty (stamp-duty) ──
  const dutyRules = stampDutyRulesFor(state);
  // Foreign-purchaser surcharge rides on the general transfer-duty surface; we
  // read it from calculateTransferDuty (the first-home surface has no surcharge).
  const transfer = calculateTransferDuty(
    { state, dutiableValue: input.purchasePrice, residency: input.residency },
    dutyRules,
  );
  const foreignSurcharge = transfer.foreignSurcharge;

  let dutyPayable: number;
  let dutyNote: string;
  if (input.firstHomeBuyer) {
    const firstHome = calculateFirstHomeDuty(
      {
        state,
        dutiableValue: input.purchasePrice,
        residency: input.residency,
        buildType: input.buildType,
      },
      dutyRules,
    );
    dutyPayable = firstHome.concessionDuty + foreignSurcharge;
    dutyNote = firstHome.concessionApplies
      ? `First-home transfer duty: general duty would be A$${firstHome.fullDuty.toLocaleString("en-AU")}; ` +
        `with the ${state} first-home ${firstHome.branch === "qld-new-build" ? "new-home " : ""}concession the duty is ` +
        `A$${firstHome.concessionDuty.toLocaleString("en-AU")} — a difference of A$${firstHome.concessionValue.toLocaleString("en-AU")}.` +
        (foreignSurcharge > 0
          ? ` Plus A$${foreignSurcharge.toLocaleString("en-AU")} foreign-purchaser surcharge.`
          : "")
      : `Above the ${state} first-home concession threshold — full general duty of ` +
        `A$${firstHome.fullDuty.toLocaleString("en-AU")} applies.` +
        (foreignSurcharge > 0
          ? ` Plus A$${foreignSurcharge.toLocaleString("en-AU")} foreign-purchaser surcharge.`
          : "");
  } else {
    dutyPayable = transfer.totalDuty;
    dutyNote =
      `General transfer duty of A$${transfer.generalDuty.toLocaleString("en-AU")} ` +
      `(${transfer.bracket.label} bracket).` +
      (foreignSurcharge > 0
        ? ` Plus A$${foreignSurcharge.toLocaleString("en-AU")} foreign-purchaser surcharge.`
        : "");
  }

  const dutyLine: CostLine = {
    id: "stamp_duty",
    label: "Transfer (stamp) duty",
    amount: dutyPayable,
    range: null,
    indicative: false,
    source: dutyRules.sources.transferDuty ?? "state revenue office",
    note: dutyNote,
  };

  // ── 3. LMI (lmi) — $0 when the 5%-scheme waiver applies ──
  const lmiRules = lmiCostRulesFor(fy);
  const lmi = calculateLmiCost(
    {
      propertyValue: input.purchasePrice,
      loanAmount: Math.max(loanAmount, 0.000001),
      firstHomeGuaranteeEligible: fivePercentEligible,
      assessmentYear: fy,
    },
    lmiRules,
  );
  const lmiRange: IndicativeRange = lmi.indicativePremium;
  const lmiLine: CostLine = {
    id: "lmi",
    label: "Lenders Mortgage Insurance (indicative)",
    amount: mid(lmiRange),
    range: lmiRange,
    indicative: true,
    source: lmiRules.sources.premiumGrid ?? "lmi indicative grid",
    note: lmi.waivedByFirstHomeGuarantee
      ? `A$0 — the Australian Government 5% Deposit Scheme waives LMI (the lender requires none).`
      : !lmi.lmiApplies
        ? `A$0 — your deposit puts the loan at or below 80% LVR, so no LMI applies.`
        : `Indicative LMI for ${lmi.lvrBand} (loan A$${round(lmi.loanAmount).toLocaleString("en-AU")}). ` +
          `This is an indicative range, never a quote — your lender's actual LMI may differ.`,
  };

  // ── 4. Misc upfront costs (this module's own dated indicative ranges) ──
  const miscItems: readonly MiscCostItem[] = [
    ...rules.miscCommon,
    ...rules.miscByState[state],
  ];
  const miscLines = miscItems.map(miscLine);

  // ── 5. FHOG (first-home-scheme) — government help, a NEGATIVE line ──
  const fhogBenefit = fhogVerdict?.maxBenefit;
  const fhogAmount =
    fhogVerdict?.eligible && fhogBenefit?.kind === "grant" ? fhogBenefit.amount : 0;
  const fhogLine: CostLine = {
    id: "fhog",
    label: "First Home Owner Grant",
    amount: fhogAmount > 0 ? -fhogAmount : 0, // negative: reduces cash to complete; +0 (not −0) when none.
    range: null,
    indicative: false,
    source:
      "https://www.firsthome.gov.au/", // scheme overview; per-state office cited in scheme module
    note: fhogAmount > 0
      ? `Less a A$${fhogAmount.toLocaleString("en-AU")} First Home Owner Grant — a gated maximum, ` +
        `not a personalised payout; confirm with your state revenue office.`
      : (fhogVerdict?.reasons[0] ??
        "No First Home Owner Grant applies on these figures."),
  };

  // ── Totals: a RANGE because LMI + misc are indicative ──
  // Exact lines (duty − FHOG) shift both ends equally; the indicative lines
  // contribute their own low/high. There is NO single "your cost is $X".
  const exactNet = dutyPayable - fhogAmount;
  const miscLow = miscItems.reduce((s, m) => s + m.range.low, 0);
  const miscHigh = miscItems.reduce((s, m) => s + m.range.high, 0);
  const totalCashToComplete: IndicativeRange = {
    low: round(exactNet + lmiRange.low + miscLow),
    high: round(exactNet + lmiRange.high + miscHigh),
  };
  const totalCashMidpoint = mid(totalCashToComplete);

  // ── 6. FHSS deposit contribution (fhss) — surfaced, NOT netted ──
  let fhssDepositContribution: HomeBuyingCostOutput["fhssDepositContribution"] = null;
  if (input.fhss) {
    // `input` is already parsed by HomeBuyingCostInputSchema, so the nested FHSS
    // object has its defaults applied (z.output shape — fields non-optional).
    const fhssIn = input.fhss;
    const fhssRules = fhssRulesFor(fy);
    const fhss = calculateFhssEligibility(
      {
        ageAtDetermination: fhssIn.ageAtDetermination,
        firstHome: fhssIn.firstHome,
        hasOwnedAuPropertyBefore: fhssIn.hasOwnedAuPropertyBefore,
        financialHardshipException: fhssIn.financialHardshipException,
        hasPreviouslyReleased: fhssIn.hasPreviouslyReleased,
        contributions: {
          concessionalThisYear: fhssIn.concessionalThisYear,
          nonConcessionalThisYear: fhssIn.nonConcessionalThisYear,
          priorYearsCounted: fhssIn.priorYearsCounted,
        },
        assessmentYear: fy,
      },
      fhssRules,
    );
    // Low-income-erosion caveat is relevant whenever the buyer contributed
    // concessional (salary-sacrifice / personal-deductible) dollars — the 85%
    // haircut going in can outweigh the benefit for a low earner. We surface it
    // as a FLAG so copy can caveat; the composer doesn't run the benefit math.
    const lowIncomeErosionCaveat = fhssIn.concessionalThisYear > 0;
    fhssDepositContribution = {
      eligible: fhss.eligible,
      maxReleasableContributions: fhss.maxReleasableContributions,
      earningsAddedOnRelease: fhss.earningsAddedOnRelease,
      lowIncomeErosionCaveat,
    };
  }

  const out: HomeBuyingCostOutput = {
    assessmentDate: input.assessmentDate,
    fy,
    state,
    purchasePrice: input.purchasePrice,
    deposit: input.deposit,
    buildType: input.buildType,
    lvr,
    dutyLine,
    lmiLine,
    miscLines,
    fhogLine,
    totalCashToComplete,
    totalCashMidpoint,
    fhssDepositContribution,
    stackingNotes: scheme.stackingNotes,
    isIndicativeTotal: true,
  };

  // Composite guard over the ASSEMBLED output — the rule holds end-to-end.
  assertNoCreditHandoff(out, surfacePlacements);
  return out;
}
